import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import MembershipActions from "../../Redux/transition/membership/membership.reducer";
import ResponsibilitiesDialog from "./ResponsibilitiesDialog";
import CustomDataTable from "../../Components/CustomDataTable";
import "../../Styles/pages/colleagues.scss";

const PAGE_SIZE = 10;
const COLLEAGUES_MOBILE_BREAKPOINT = 1050;

const ColleaguesList = () => {
  const dispatch = useDispatch();
  const defMembership = useSelector((state) => state.defMembership.defMembership)
  const fetchingAllMembership = useSelector((state) => state.memberships.fetchingAll)
  const memberships = useSelector((state) => state.memberships.memberships)
  const updating = useSelector((state) => state.memberships.updating)
  const errorUpdating = useSelector((state) => state.memberships.errorUpdating)
  const deleting = useSelector((state) => state.memberships.deleting)
  const errorDeleting = useSelector((state) => state.memberships.errorDeleting)
  const { t } = useTranslation("common");
  const loading = !defMembership || fetchingAllMembership
  const toast = useRef(null);

  const [pageState, setPageState] = useState({ page: 0, first: 0, size: PAGE_SIZE });
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [responsibilitiesDialogVisible, setResponsibilitiesDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingMember, setDeletingMember] = useState(false);
  const [isMobileStack, setIsMobileStack] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= COLLEAGUES_MOBILE_BREAKPOINT
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(max-width: ${COLLEAGUES_MOBILE_BREAKPOINT}px)`);
    const onBreakpointChange = (event) => setIsMobileStack(event.matches);
    onBreakpointChange(mediaQuery);
    mediaQuery.addEventListener("change", onBreakpointChange);
    return () => mediaQuery.removeEventListener("change", onBreakpointChange);
  }, []);

  const membershipList = useMemo(
    () => (Array.isArray(memberships) ? memberships : memberships?.content) ?? [],
    [memberships]
  );
  const totalRecords = memberships?.totalElements ?? membershipList.length;
  const canManageMembers = defMembership?.role?.toLowerCase() === "owner";

  const roleOptions = useMemo(() => [
    { roleName: t('colleague.manager'), code: "MANAGER" },
    { roleName: t('colleague.viewer'), code: "VIEWER" },
    { roleName: t('colleague.admin'), code: "ADMIN" },
  ], [t]);

  const getRoleOption = useCallback((role) => {
    if (!role) return null;
    return roleOptions.find((option) => option.code === role.toUpperCase()) || null;
  }, [roleOptions]);

  const fetchMemberships = useCallback((page = 0, size = PAGE_SIZE) => {
    if (defMembership?.business?.id) {
      dispatch(MembershipActions.membershipAllRequest(defMembership.business.id, {
        page,
        size,
        sort: "id,desc",
      }));
    }
  }, [dispatch, defMembership]);

  useEffect(() => {
    fetchMemberships(pageState.page, pageState.size);
  }, [fetchMemberships, pageState.page, pageState.size]);

  useEffect(() => {
    if (!saving || updating) {
      return;
    }

    if (errorUpdating) {
      toast.current?.show({
        severity: "error",
        summary: t('failed'),
        detail: errorUpdating.title || t('colleague.error'),
        life: 3000,
      });
    } else {
      toast.current?.show({
        severity: "success",
        summary: t('colleague.success'),
        detail: t('colleague.role_update_success'),
        life: 3000,
      });
      setEditDialogVisible(false);
      setSelectedMembership(null);
      setSelectedRole(null);
      fetchMemberships(pageState.page, pageState.size);
    }

    setSaving(false);
  }, [saving, updating, errorUpdating, fetchMemberships, pageState.page, pageState.size, t]);

  useEffect(() => {
    if (!deletingMember || deleting) {
      return;
    }

    if (errorDeleting) {
      toast.current?.show({
        severity: "error",
        summary: t('failed'),
        detail: errorDeleting.title || t('colleague.error'),
        life: 3000,
      });
    } else {
      toast.current?.show({
        severity: "success",
        summary: t('colleague.success'),
        detail: t('colleague.member_delete_success'),
        life: 3000,
      });
      setSelectedMembership(null);
      fetchMemberships(pageState.page, pageState.size);
    }

    setDeletingMember(false);
  }, [deletingMember, deleting, errorDeleting, fetchMemberships, pageState.page, pageState.size, t]);

  const onPage = (e) => {
    setPageState({
      page: e.page,
      first: e.first,
      size: e.rows,
    });
  };

  const openEditDialog = (membership) => {
    setSelectedMembership(membership);
    setSelectedRole(getRoleOption(membership.role));
    setEditDialogVisible(true);
  };

  const openResponsibilitiesDialog = (membership) => {
    setSelectedMembership(membership);
    setResponsibilitiesDialogVisible(true);
  };

  const openDeleteDialog = (membership) => {
    setSelectedMembership(membership);
    setDeleteDialogVisible(true);
  };

  const closeEditDialog = () => {
    setEditDialogVisible(false);
    setSelectedMembership(null);
    setSelectedRole(null);
  };

  const closeResponsibilitiesDialog = () => {
    setResponsibilitiesDialogVisible(false);
    setSelectedMembership(null);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogVisible(false);
    if (!deletingMember) {
      setSelectedMembership(null);
    }
  };

  const showToast = (toastConfig) => {
    toast.current?.show({ life: 3000, ...toastConfig });
  };

  const handleSaveRole = () => {
    if (!selectedMembership || !selectedRole) {
      return;
    }

    dispatch(MembershipActions.membershipUpdateRequest({
      ...selectedMembership,
      role: selectedRole.code,
    }));
    setSaving(true);
  };

  const handleConfirmDelete = () => {
    if (!selectedMembership?.id) {
      return;
    }

    dispatch(MembershipActions.membershipDeleteRequest(selectedMembership.id));
    setDeleteDialogVisible(false);
    setDeletingMember(true);
  };

  const roleBodyTemplate = (data) => {
    const role = (data.role || '').toLowerCase();
    if (!role) {
      return <div className="text-left"><span className="table_data">—</span></div>;
    }

    return (
      <div className="text-left">
        <span className={`colleague-role-badge ${role}`}>
          <i className="pi pi-user colleague-role-icon" aria-hidden="true" />
          {data.role}
        </span>
      </div>
    );
  };

  const nameBodyTemplate = (data) => (
    <div className="text-left">
      <span className="table_data">{data.user?.name || 'N/A'}</span>
    </div>
  );

  const emailBodyTemplate = (data) => (
    <div className="text-left">
      <span className="table_data">{data.user?.email || 'N/A'}</span>
    </div>
  );

  const responsibilitiesBodyTemplate = (data) => {
    const role = (data.role || '').toLowerCase();
    const hasResponsibilities = role === 'manager' || role === 'viewer';
    const isOwnerRole = role === 'owner';

    if (!canManageMembers || isOwnerRole || !hasResponsibilities) {
      return <div className="text-left"><span className="table_data">—</span></div>;
    }

    return (
      <div className="text-left">
        <Button
          label={t('colleague.responsibilities')}
          icon="pi pi-sitemap"
          className="p-button-outlined p-button-secondary colleague-responsibilities-btn"
          onClick={() => openResponsibilitiesDialog(data)}
        />
      </div>
    );
  };

  const actionBodyTemplate = (data) => {
    const isOwnerRole = (data.role || '').toLowerCase() === 'owner';
    const isDeletingThis = deleting && selectedMembership?.id === data.id;

    if (!canManageMembers || isOwnerRole) {
      return null;
    }

    return (
      <div className="colleague-table-actions text-left">
        <Button
          icon="pi pi-user-edit"
          className="p-button-outlined p-button-secondary colleague-edit-role-btn"
          tooltip={t('colleague.edit')}
          tooltipOptions={{ position: "bottom", className: "colleague-edit-role-tooltip" }}
          onClick={() => openEditDialog(data)}
          disabled={deleting}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-outlined p-button-danger colleague-delete-btn"
          tooltip={t('colleague.remove')}
          tooltipOptions={{ position: "bottom", className: "colleague-delete-tooltip" }}
          onClick={() => openDeleteDialog(data)}
          loading={isDeletingThis}
          disabled={deleting}
        />
      </div>
    );
  };

  const mobileSummaryTemplate = (data) => (
    <div className="colleagues-mobile-summary">
      <div className="colleagues-mobile-section">
        <span className="colleagues-mobile-label">{t("colleague.role")}</span>
        {roleBodyTemplate(data)}
      </div>
      <div className="colleagues-mobile-section">
        <span className="colleagues-mobile-label">{t("colleague.name")}</span>
        {nameBodyTemplate(data)}
      </div>
      <div className="colleagues-mobile-section">
        <span className="colleagues-mobile-label">{t("colleague.email")}</span>
        {emailBodyTemplate(data)}
      </div>
      <div className="colleagues-mobile-section">
        <span className="colleagues-mobile-label">{t("colleague.responsibilities")}</span>
        {responsibilitiesBodyTemplate(data)}
      </div>
      {canManageMembers && (data.role || '').toLowerCase() !== 'owner' && (
        <div className="colleagues-mobile-action-row">
          {actionBodyTemplate(data)}
        </div>
      )}
    </div>
  );

  const desktopColumns = [
    {
      field: "role",
      header: () => <p className="table_header">{t("colleague.role")}</p>,
      body: roleBodyTemplate,
      align: "left",
      alignHeader: "left",
      style: { minWidth: "8rem", textAlign: "left" },
    },
    {
      field: "name",
      header: () => <p className="table_header">{t("colleague.name")}</p>,
      body: nameBodyTemplate,
      align: "left",
      alignHeader: "left",
      style: { minWidth: "10rem", textAlign: "left" },
    },
    {
      field: "email",
      header: () => <p className="table_header">{t("colleague.email")}</p>,
      body: emailBodyTemplate,
      align: "left",
      alignHeader: "left",
      style: { minWidth: "14rem", textAlign: "left" },
    },
    {
      field: "responsibilities",
      header: () => <p className="table_header">{t("colleague.responsibilities")}</p>,
      body: responsibilitiesBodyTemplate,
      align: "left",
      alignHeader: "left",
      style: { minWidth: "10rem", textAlign: "left" },
    },
    {
      field: "actions",
      header: () => <p className="table_header">{t("common.actions")}</p>,
      body: actionBodyTemplate,
      align: "left",
      alignHeader: "left",
      bodyClassName: "text-left",
      style: { minWidth: "6rem", width: "6rem", textAlign: "left" },
    },
  ];

  const columns = isMobileStack
    ? [{
      field: "mobileSummary",
      header: () => <p className="table_header">{t("colleague.name")}</p>,
      body: mobileSummaryTemplate,
      className: "colleagues-mobile-summary-column",
      align: "left",
      alignHeader: "left",
    }]
    : desktopColumns;

  const editDialogFooter = (
    <div className="flex justify-content-end gap-2 edit-role-dialog-footer">
      <Button
        label={t('cancel')}
        icon="pi pi-times"
        className="p-button-text"
        onClick={closeEditDialog}
        disabled={updating}
      />
      <Button
        label={t('common.save')}
        className="edit-role-save-btn"
        onClick={handleSaveRole}
        loading={updating}
        disabled={!selectedRole || updating}
      />
    </div>
  );

  const memberName = selectedMembership?.user?.name || selectedMembership?.user?.email || "";

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog
        visible={deleteDialogVisible}
        onHide={closeDeleteDialog}
        message={t('colleague.member_delete_confirmation', { name: memberName })}
        header={t('colleague.confirmation_header')}
        icon="pi pi-exclamation-triangle"
        accept={handleConfirmDelete}
        reject={closeDeleteDialog}
        acceptLabel={t('colleague.remove')}
        rejectLabel={t('cancel')}
        rejectIcon="pi pi-times"
        acceptClassName="colleague-delete-confirm-accept"
        rejectClassName="p-button-text colleague-delete-confirm-reject"
        className="tier_dialog colleague-delete-confirm"
        breakpoints={{ "960px": "75vw", "640px": "95vw" }}
        style={{ width: "450px" }}
      />
      <Dialog
        header={t('colleague.edit')}
        visible={editDialogVisible}
        style={{ width: "370px" }}
        breakpoints={{ "960px": "75vw", "640px": "95vw" }}
        onHide={closeEditDialog}
        footer={editDialogFooter}
        className="tier_dialog edit-role-dialog"
      >
        <div className="inviteRole p-fluid edit-role-field" style={{ padding: "0.5rem 0" }}>
          <label htmlFor="editRole" className="block mb-2 edit-role-label">{t('colleague.role')}</label>
          <Dropdown
            id="editRole"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.value)}
            options={roleOptions}
            optionLabel="roleName"
            placeholder={t('colleague.select_role')}
            className="w-full"
            style={{ width: "100%" }}
          />
        </div>
      </Dialog>
      <ResponsibilitiesDialog
        visible={responsibilitiesDialogVisible}
        onHide={closeResponsibilitiesDialog}
        membership={selectedMembership}
        businessId={defMembership?.business?.id}
        onToast={showToast}
      />
      <CustomDataTable
        key={`colleagues-users-${isMobileStack}`}
        columns={columns}
        value={membershipList}
        loading={loading}
        lazy
        paginator
        rows={pageState.size}
        first={pageState.first}
        totalRecords={totalRecords}
        onPage={onPage}
        responsiveLayout="stack"
        breakpoint={`${COLLEAGUES_MOBILE_BREAKPOINT}px`}
        tableClassName="colleague-users-table"
      />
    </>
  );
};
export default (ColleaguesList);
